/**
 * @file GenderPicker.tsx
 * @brief Gender selection — pill trigger + searchable modal sheet.
 *
 * @details
 * Mirrors mobile `VZGenderPicker` and company-admin language picker UX:
 * count in title, per-row icons, contextual subtitle support.
 *
 * @author DES
 * @date 19-06-2026
 */

import { useMemo, useState } from "react";
import { ChevronDown, ChevronsUpDown } from "lucide-react";
import { GenderIcon } from "@/components/ui/GenderIcon";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { GENDERS, type GenderOption } from "@/shared/data/genders";
import { cn } from "@/shared/theme/cn";

interface GenderPickerProps {
  value?: string | null;
  onChange: (genderCode: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  /** When false, clearing is disabled (required fields). Default true. */
  clearable?: boolean;
  /** Override modal title (default includes option count). */
  sheetTitle?: string;
  /** Override modal subtitle for context. */
  sheetSubtitle?: string;
  /**
   * `field` (01-10-2026): the bare setup-page row (icon, 17px value, chevron,
   * no box), as Boardroom setup's pickers inside a Fieldset.
   */
  variant?: "pill" | "field";
}

interface GenderPickerItem extends PickerSheetItem {
  gender: GenderOption;
}

function findGender(value?: string | null): GenderOption | undefined {
  if (!value) return undefined;
  return GENDERS.find((g) => g.code === value);
}

export function GenderPicker({
  value,
  onChange,
  placeholder = "Not set",
  disabled,
  size = "md",
  clearable = true,
  sheetTitle,
  sheetSubtitle = "Choose how this participant should be voiced in sessions.",
  variant = "pill",
}: GenderPickerProps) {
  const [open, setOpen] = useState(false);

  const matched = useMemo(() => findGender(value), [value]);

  const items: GenderPickerItem[] = useMemo(
    () =>
      GENDERS.map((gender) => ({
        id: gender.code,
        label: gender.label,
        gender,
      })),
    [],
  );

  const modalTitle = sheetTitle ?? `Select gender (${GENDERS.length})`;

  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  return (
    <>
      <PickerSheet<GenderPickerItem>
        anchor={
          variant === "field" ? (
            <button
              type="button"
              disabled={disabled}
              className="flex min-h-11 w-full items-center gap-2 rounded-md py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <GenderIcon code={matched?.code ?? null} size="sm" />
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-[17px] font-medium",
                  !matched && "text-muted-foreground",
                )}
              >
                {matched?.label ?? placeholder}
              </span>
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "flex w-full items-center justify-between rounded-full border border-input bg-field px-5 text-sm transition-colors",
                triggerHeight,
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                "disabled:cursor-not-allowed disabled:opacity-50",
                !matched && "text-muted-foreground",
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <GenderIcon code={matched?.code ?? null} size="sm" />
                <span className="truncate">{matched?.label ?? placeholder}</span>
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </button>
          )
        }
        open={open}
        onOpenChange={setOpen}
        title={modalTitle}
        subtitle={sheetSubtitle}
        searchPlaceholder="Search genders…"
        items={items}
        selectedId={matched?.code ?? null}
        onSelect={(item) => onChange(item.id)}
        onClear={clearable ? () => onChange(null) : undefined}
        clearable={clearable}
        renderLeftAccessory={(item) => <GenderIcon code={item.gender.code} size="lg" />}
      />
    </>
  );
}
